import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  Construction as ConstructionIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  WorkspacePremium as PremiumIcon,
} from '@mui/icons-material';

import { useAuth } from '../services/AuthService';
import ProfileService from '../services/ProfileService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import TrainingService from '../services/TrainingService';

const EMPTY_ENROLLMENT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  suburb: '',
  postcode: '',
  dateOfBirth: '',
  consentToMarketing: false,
};

const enrollmentSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  phone: yup.string().nullable(),
  suburb: yup.string().nullable(),
  postcode: yup.string().nullable(),
  dateOfBirth: yup.string().nullable(),
  consentToMarketing: yup.boolean().default(false),
});

const formatDate = (value) => {
  try {
    return new Intl.DateTimeFormat('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatPrice = (value, currency = 'AUD') => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';

  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${value}`;
  }
};

const getSessionLocation = (session) => session?.locationLabel || session?.location || '';

const getErrorMessage = (error, fallbackMessage) => (
  error?.body?.message ||
  error?.body?.error ||
  error?.message ||
  fallbackMessage
);

const EMPTY_STATE_MESSAGE = 'We are working with great partners to find the best deals in training, tickets and certifications. Stay tuned.';

const TrainingLandingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [checkoutNotice, setCheckoutNotice] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(enrollmentSchema),
    defaultValues: EMPTY_ENROLLMENT_VALUES,
  });

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      setLoading(true);
      setError('');

      const [courseResult, sessionResult] = await Promise.allSettled([
        TrainingService.getCourses(),
        TrainingService.getPublicSessions(),
      ]);

      if (!active) return;

      const nextCourses = courseResult.status === 'fulfilled' && Array.isArray(courseResult.value)
        ? courseResult.value
        : [];
      const nextSessions = sessionResult.status === 'fulfilled' && Array.isArray(sessionResult.value)
        ? sessionResult.value
        : [];

      setCourses(nextCourses);
      setSessions(nextSessions);

      const errorsToShow = [];
      if (courseResult.status === 'rejected') {
        errorsToShow.push(getErrorMessage(courseResult.reason, 'Unable to load the live course catalogue right now.'));
      }
      if (sessionResult.status === 'rejected') {
        errorsToShow.push(getErrorMessage(sessionResult.reason, 'Unable to load the latest training dates right now.'));
      }

      if (errorsToShow.length > 0) {
        setError(errorsToShow[0]);
      }

      setLoading(false);
    };

    loadData();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const courseId = searchParams.get('courseId');
    const sessionId = searchParams.get('sessionId');
    if (!courseId || !sessionId || courses.length === 0) return;

    const course = courses.find((item) => item.id === courseId);
    const session = sessions.find((item) => item.id === sessionId);
    if (!course || !session) return;

    setSelectedCourse(course);
    setSelectedSession(session);
    setSubmitError('');
    setDialogOpen(true);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('courseId');
      next.delete('sessionId');
      return next;
    }, { replace: true });
  }, [courses, sessions, searchParams, setSearchParams]);

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus !== 'cancelled') return;

    setCheckoutNotice('Payment was not completed. Please select a session again if you would like to try checkout.');
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('checkout');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let active = true;

    const prefill = async () => {
      if (!dialogOpen) return;

      setSubmitError('');

      if (!isAuthenticated) {
        return;
      }

      try {
        setPrefillLoading(true);
        const [profile, jobSeekerProfile] = await Promise.all([
          new ProfileService().getRecordsByUserId(user?.sub).catch(() => null),
          new JobSeekerProfileService().getRecordsByUserId(user?.sub).catch(() => null),
        ]);

        if (!active) return;

        reset({
          firstName: profile?.firstName || user?.given_name || '',
          lastName: profile?.lastName || user?.family_name || '',
          email: profile?.email || user?.email || '',
          phone: profile?.phone || user?.phone_number || '',
          suburb: jobSeekerProfile?.suburb || '',
          postcode: jobSeekerProfile?.postcode || '',
          dateOfBirth: '',
          consentToMarketing: false,
        });
      } finally {
        if (active) {
          setPrefillLoading(false);
        }
      }
    };

    prefill();

    return () => {
      active = false;
    };
  }, [dialogOpen, isAuthenticated, reset, user]);

  const sessionsByCourse = useMemo(() => (
    sessions.reduce((acc, session) => {
      if (!acc[session.courseId]) acc[session.courseId] = [];
      acc[session.courseId].push(session);
      return acc;
    }, {})
  ), [sessions]);

  const uniqueCategories = useMemo(() => (
    [...new Set(courses.map((course) => course.category).filter(Boolean))].sort()
  ), [courses]);

  const uniqueLocations = useMemo(() => (
    [...new Set(
      sessions
        .map((session) => getSessionLocation(session).trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b))
  ), [sessions]);

  const filteredSessionsByCourse = useMemo(() => (
    Object.fromEntries(
      Object.entries(sessionsByCourse).map(([courseId, courseSessions]) => [
        courseId,
        locationFilter
          ? courseSessions.filter((session) => getSessionLocation(session) === locationFilter)
          : courseSessions,
      ])
    )
  ), [locationFilter, sessionsByCourse]);

  const filteredCourses = useMemo(() => {
    let result = [...courses];

    if (textSearch.trim()) {
      const query = textSearch.toLowerCase();
      result = result.filter((course) => (
        course.title?.toLowerCase().includes(query) ||
        course.description?.toLowerCase().includes(query)
      ));
    }

    if (categoryFilter) {
      result = result.filter((course) => course.category === categoryFilter);
    }

    if (locationFilter) {
      result = result.filter((course) => (
        (sessionsByCourse[course.id] || []).some((session) => getSessionLocation(session) === locationFilter)
      ));
    }

    result.sort((a, b) => {
      const orderDelta = (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
      if (orderDelta !== 0) return orderDelta;
      return (a.title || '').localeCompare(b.title || '');
    });

    return result;
  }, [categoryFilter, courses, locationFilter, sessionsByCourse, textSearch]);

  const hasCourses = courses.length > 0;
  const hasFilteredCourses = filteredCourses.length > 0;

  const selectedSessionPartnerText = useMemo(() => {
    if (!selectedSession) return '';
    const provider = selectedSession.partnerName || 'Training provider';
    const rto = selectedSession.partnerRtoNumber ? `RTO number: ${selectedSession.partnerRtoNumber}` : 'RTO number not provided';
    return `Training certification provided by ${provider}, ${rto}`;
  }, [selectedSession]);

  const handleEnrollClick = (course, session) => {
    setSelectedCourse(course);
    setSelectedSession(session);
    setSubmitError('');
    setDialogOpen(true);
  };

  const onSubmit = async (values) => {
    if (!selectedCourse || !selectedSession) {
      setSubmitError('Select a course session before continuing to payment.');
      return;
    }

    setSubmitError('');
    setCheckoutNotice('');

    try {
      const [profile, jobSeekerProfile] = isAuthenticated
        ? await Promise.all([
            new ProfileService().getRecordsByUserId(user?.sub).catch(() => null),
            new JobSeekerProfileService().getRecordsByUserId(user?.sub).catch(() => null),
          ])
        : [null, null];

      const payload = {
        courseId: selectedCourse.id,
        sessionId: selectedSession.id,
        profileId: isAuthenticated ? profile?.id : undefined,
        profileSnapshot: isAuthenticated ? (profile || undefined) : undefined,
        jobSeekerSnapshot: isAuthenticated ? (jobSeekerProfile || undefined) : undefined,
        ...values,
      };

      const response = await TrainingService.createEnrollment(payload);
      const enrollment = response.enrollment;
      const checkout = await TrainingService.startCheckout(enrollment.id, { accessToken: response.accessToken });

      if (checkout?.checkoutUrl) {
        window.location.assign(checkout.checkoutUrl);
        return;
      }

      const confirmationParams = new URLSearchParams({ enrollmentId: enrollment.id });
      navigate(`/training/confirmation?${confirmationParams.toString()}`);
    } catch (submissionError) {
      setSubmitError(getErrorMessage(submissionError, 'Unable to continue to payment right now.'));
    }
  };

  return (
    <Box
      data-testid="training-landing-page"
      sx={{ py: { xs: 5, md: 8 }, background: 'linear-gradient(180deg, rgba(29,175,90,0.08), transparent 30%)' }}
    >
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
              <Chip icon={<SchoolIcon />} label="Training Marketplace" color="primary" variant="outlined" />
              <Chip label="No account required" color="success" variant="outlined" />
            </Stack>
            <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.1, maxWidth: 820 }}>
              Get site-ready faster with construction tickets, certifications, and licensed training.
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2, maxWidth: 780 }}>
              Labourando promotes certified training courses so workers can find the right certification, pick a date, and book securely in one place.
            </Typography>
            {hasCourses ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button variant="contained" size="large" href="#courses">
                  View courses
                </Button>
                <Button variant="outlined" size="large" href="#how-it-works">
                  How it works
                </Button>
              </Stack>
            ) : null}
          </Box>

          {(loading || hasCourses) ? (
            <Paper data-testid="training-filters" variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  data-testid="training-search-input"
                  size="small"
                  label="Search courses"
                  placeholder="Search by course name or description"
                  value={textSearch}
                  onChange={(event) => setTextSearch(event.target.value)}
                  sx={{ minWidth: 280, flex: 1 }}
                />
                {uniqueCategories.length > 1 && (
                  <TextField
                    data-testid="training-category-filter"
                    select
                    size="small"
                    label="Category"
                    InputLabelProps={{ shrink: true }}
                    value={categoryFilter}
                    onChange={(event) => setCategoryFilter(event.target.value)}
                    sx={{ minWidth: 200 }}
                    SelectProps={{ native: true }}
                  >
                    <option value="">All categories</option>
                    {uniqueCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </TextField>
                )}
                <TextField
                  data-testid="training-location-filter"
                  select
                  size="small"
                  label="Location"
                  InputLabelProps={{ shrink: true }}
                  value={locationFilter}
                  onChange={(event) => setLocationFilter(event.target.value)}
                  sx={{ minWidth: 240 }}
                  SelectProps={{ native: true }}
                  disabled={uniqueLocations.length === 0}
                >
                  <option value="">All locations</option>
                  {uniqueLocations.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </TextField>
              </Stack>
            </Paper>
          ) : null}

          {checkoutNotice ? <Alert severity="info">{checkoutNotice}</Alert> : null}
          {error && hasCourses ? <Alert severity="warning">{error}</Alert> : null}

          {loading ? (
            <Grid data-testid="training-course-grid" container spacing={3} id="courses">
              {Array.from({ length: 3 }).map((_, index) => (
                <Grid key={index} size={{ xs: 12, md: 4 }}>
                  <Card data-testid="training-course-skeleton">
                    <CardContent>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="rounded" height={180} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : hasCourses ? (
            <Grid data-testid="training-course-grid" container spacing={3} id="courses">
              {hasFilteredCourses ? filteredCourses.map((course) => {
                const courseSessions = filteredSessionsByCourse[course.id] || [];

                return (
                  <Grid key={course.id} size={{ xs: 12, md: 4 }}>
                    <Card data-testid={`training-course-${course.id}`} sx={{ height: '100%' }}>
                      <CardContent sx={{ height: '100%' }}>
                        <Stack spacing={2} sx={{ height: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={0.5}>
                              <Chip label={course.category} size="small" />
                            </Stack>
                            {course.isActive ? (
                              <Chip icon={<CheckCircleIcon />} label="Live" color="success" size="small" />
                            ) : (
                              <Chip icon={<LockIcon />} label="Paused" size="small" />
                            )}
                          </Stack>

                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{course.title}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {course.description}
                            </Typography>
                          </Box>

                          <Box>
                            {course.imageUrl ? (
                              <img src={course.imageUrl} alt={course.title} style={{ width: '100%', borderRadius: 4 }} />
                            ) : null}
                          </Box>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip label={course.certificationType || 'Certification'} size="small" />
                            <Chip label={course.duration || 'Flexible date'} size="small" />
                            <Chip label={course.deliveryMode || 'Classroom'} size="small" />
                          </Stack>

                          <Divider />

                          <Stack spacing={1}>
                            {course.benefits?.slice(0, 3).map((benefit) => (
                              <Typography key={benefit} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon fontSize="small" color="primary" /> {benefit}
                              </Typography>
                            ))}
                          </Stack>

                          <Box sx={{ mt: 'auto' }}>
                            {courseSessions.length > 0 ? (
                              courseSessions.slice(0, 2).map((session) => (
                                <Paper data-testid={`training-session-${session.id}`} key={session.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {formatDate(session.startAt)}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {getSessionLocation(session) || 'Training location'}
                                      </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                        {formatPrice(session.price, session.currency)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {session.seatsRemaining} spots left
                                      </Typography>
                                    </Box>
                                  </Stack>
                                  <Button
                                    data-testid={`training-enroll-${session.id}`}
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 1 }}
                                    onClick={() => handleEnrollClick(course, session)}
                                    disabled={session.seatsRemaining <= 0}
                                  >
                                    {session.seatsRemaining <= 0 ? 'Sold out' : 'Enroll'}
                                  </Button>
                                </Paper>
                              ))
                            ) : (
                              <Alert severity="info" sx={{ mb: 1 }}>
                                Upcoming dates will appear here once they are published.
                              </Alert>
                            )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              }) : (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    No training courses match your current filters.
                  </Alert>
                </Grid>
              )}
            </Grid>
          ) : (
            <Paper data-testid="training-empty-state" variant="outlined" sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <SchoolIcon color="primary" sx={{ fontSize: 40 }} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Training is coming soon
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
                  {EMPTY_STATE_MESSAGE}
                </Typography>
              </Stack>
            </Paper>
          )}

          {hasCourses ? (
            <Box id="how-it-works">
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>How it works</Typography>
              <Grid container spacing={3}>
                {[
                  { icon: ConstructionIcon, title: 'Choose a course', text: 'Pick the certificate or ticket that matches your work goals.' },
                  { icon: CalendarMonthIcon, title: 'Select a date', text: 'Review availability, spots, and the session location.' },
                  { icon: PremiumIcon, title: 'Pay securely', text: 'Complete payment through Stripe hosted checkout and return to Labourando.' },
                ].map((step) => (
                  <Grid key={step.title} size={{ xs: 12, md: 4 }}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <step.icon color="primary" />
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>{step.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{step.text}</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}
        </Stack>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle data-testid="training-enrollment-dialog-title">Enroll in {selectedCourse?.title}</DialogTitle>
        <DialogContent dividers>
          {selectedSession ? (
            <Alert data-testid="training-selected-session-alert" severity="info" sx={{ mb: 2 }}>
              {formatDate(selectedSession.startAt)} · {selectedSession.locationLabel || selectedSession.location || 'Training location'} · {selectedSession.seatsRemaining} spots left
            </Alert>
          ) : null}
          {prefillLoading ? <Alert data-testid="training-prefill-alert" severity="info">Prefilling your details from your profile...</Alert> : null}
          {!isAuthenticated ? (
            <Alert data-testid="training-guest-alert" severity="success" sx={{ mt: prefillLoading ? 2 : 0 }}>
              You can complete this booking as a guest. No signup is required.
            </Alert>
          ) : null}
          {submitError ? <Alert data-testid="training-submit-error" severity="error" sx={{ mt: 2 }}>{submitError}</Alert> : null}
          <Stack
            data-testid="training-enrollment-form"
            spacing={2}
            sx={{ mt: 2 }}
            component="form"
            id="training-enrollment-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <TextField
              inputProps={{ 'data-testid': 'training-first-name-input' }}
              label="First name"
              InputLabelProps={{ shrink: true }}
              {...register('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              fullWidth
            />
            <TextField
              inputProps={{ 'data-testid': 'training-last-name-input' }}
              label="Last name"
              InputLabelProps={{ shrink: true }}
              {...register('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              fullWidth
            />
            <TextField
              inputProps={{ 'data-testid': 'training-email-input' }}
              label="Email"
              InputLabelProps={{ shrink: true }}
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              fullWidth
            />
            <TextField
              inputProps={{ 'data-testid': 'training-phone-input' }}
              label="Phone"
              InputLabelProps={{ shrink: true }}
              {...register('phone')}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField inputProps={{ 'data-testid': 'training-suburb-input' }} label="Suburb" InputLabelProps={{ shrink: true }} {...register('suburb')} fullWidth />
              <TextField inputProps={{ 'data-testid': 'training-postcode-input' }} label="Postcode" InputLabelProps={{ shrink: true }} {...register('postcode')} fullWidth />
            </Stack>
            <TextField
              inputProps={{ 'data-testid': 'training-date-of-birth-input' }}
              label="Date of birth"
              type="date"
              InputLabelProps={{ shrink: true }}
              {...register('dateOfBirth')}
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox inputProps={{ 'data-testid': 'training-consent-checkbox' }} {...register('consentToMarketing')} />}
              label="Send me booking updates and related course recommendations"
            />
            {selectedSessionPartnerText ? (
              <Typography data-testid="training-provider-caption" variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {selectedSessionPartnerText}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-testid="training-cancel-button" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button data-testid="training-continue-to-payment-button" type="submit" form="training-enrollment-form" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Continue to payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingLandingPage;
